import React, { useEffect, useState } from 'react'

function useSafeDispatch(dispatch) {
  const mounted = React.useRef(false)
  React.useLayoutEffect(() => {
    mounted.current = true
    return () => (mounted.current = false)
  }, [])
  return React.useCallback(
    (...args) => (mounted.current ? dispatch(...args) : void 0),
    [dispatch],
  )
}

// Example usage:
// const {data, error, status, run} = useAsync()
// React.useEffect(() => {
//   run(fetchPokemon(pokemonName))
// }, [pokemonName, run])
const defaultInitialState = {status: 'idle', data: null, error: null}
function useAsync(initialState) {
  const initialStateRef = React.useRef({
    ...defaultInitialState,
    ...initialState,
  })
  const [{status, data, error}, setState] = React.useReducer(
    (s, a) => ({...s, ...a}),
    initialStateRef.current,
  )

  const safeSetState = useSafeDispatch(setState)

  const setData = React.useCallback(
    data => safeSetState({data, status: 'resolved'}),
    [safeSetState],
  )
  const setError = React.useCallback(
    error => safeSetState({error, status: 'rejected'}),
    [safeSetState],
  )
  const reset = React.useCallback(() => safeSetState(initialStateRef.current), [
    safeSetState,
  ])

  const run = React.useCallback(
    promise => {
      if (!promise || !promise.then) {
        throw new Error(
          `The argument passed to useAsync().run must be a promise. Maybe a function that's passed isn't returning anything?`,
        )
      }
      safeSetState({status: 'pending'})
      return promise.then(
        data => {
          setData(data)
          return data
        },
        error => {
          setError(error)
          return Promise.reject(error)
        },
      )
    },
    [safeSetState, setData, setError],
  )

  return {
    // using the same names that react-query uses for convenience
    isIdle: status === 'idle',
    isLoading: status === 'pending',
    isError: status === 'rejected',
    isSuccess: status === 'resolved',

    setData,
    setError,
    error,
    status,
    data,
    run,
    reset,
  }
}

function useSessionStorage(key) {
  const [state, setState] = useState(window.sessionStorage.getItem(key) || '');

   // on every render, re-subscribe to the storage event
   useEffect(() => {
    const handler = (e) => {
      if (e.type === 'session-storage') {
        setState(readValue());
      }
    };

    const readValue = () => {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        console.warn(`Error reading sessionStorage key “${key}”:`, error)
        return state;
      }
    }
  
    window.addEventListener('storage', handler);
    window.addEventListener('session-storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('session-storage', handler);
    };
  }, [key, state]);

  useEffect(() => {
    window.sessionStorage.setItem(key, state);
    window.dispatchEvent(new Event('session-storage'));
  }, [key, state]);

  return [state, setState];
}

function useLocalStorage(key, initialValue="") {
  const [state, setState] = useState(localStorage.getItem(key) || initialValue);

   // on every render, re-subscribe to the storage event
   useEffect(() => {
    const handler = (e) => {
      if (e.type === 'local-storage') {
        setState(readValue());
      }
    };

    const readValue = () => {
      try {
        return localStorage.getItem(key);
      } catch (error) {
        console.warn(`Error reading localStorage key “${key}”:`, error)
        return state;
      }
    }
  
    window.addEventListener('storage', handler);
    window.addEventListener('local-storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('local-storage', handler);
    };
  }, [key, state]);

  useEffect(() => {
    localStorage.setItem(key, state);
    window.dispatchEvent(new Event('local-storage'));
  }, [key, state]);

  return [state, setState];
}

export { useAsync, useLocalStorage, useSessionStorage }

